import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Row, Col, Typography, Card, Select, Button, Slider, Input, Tag, Divider, Space, Grid } from 'antd';
import { 
  AppstoreOutlined, 
  DollarOutlined, 
  DashboardOutlined, 
  ClockCircleOutlined,
  ReloadOutlined,
  RightOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { Option } = Select;
const { useBreakpoint } = Grid;

const MILEAGE_VARIANCES = [2, 5, 10, 15, 20];
const RADIUS_OPTIONS = [25, 50, 100, 250, 500];

/* -------------------- Metric card (dark, colored) -------------------- */
function MetricPro({ icon, label, value, unit, subTag, tone = "indigo" }) {
  const palette = {
    indigo: { chip: "rgba(99,102,241,.14)", ring: "rgba(99,102,241,.45)", fg: "#a5b4fc" },
    blue:   { chip: "rgba(59,130,246,.14)", ring: "rgba(59,130,246,.45)", fg: "#93c5fd" },
    amber:  { chip: "rgba(245,158,11,.14)", ring: "rgba(245,158,11,.45)", fg: "#fcd34d" },
    teal:   { chip: "rgba(45,212,191,.14)", ring: "rgba(45,212,191,.45)", fg: "#5eead4" },
    gray:   { chip: "rgba(148,163,184,.12)", ring: "rgba(148,163,184,.35)", fg: "#cbd5e1" },
  }[tone];

  return (
    <Card
      bordered={false}
      style={{
        borderRadius: 14,
        background: "linear-gradient(180deg, #0f1422 0%, #0b101a 100%)",
        border: "1px solid rgba(255,255,255,.06)",
        boxShadow: "0 10px 26px rgba(0,0,0,.35)",
        transition: "transform .15s ease, box-shadow .15s ease, border-color .15s",
        position: "relative",
        overflow: "hidden",
      }}
      bodyStyle={{ padding: 14, position: "relative", zIndex: 2 }}
      onMouseEnter={(e) => (e.currentTarget.style.borderColor = palette.ring)}
      onMouseLeave={(e) => (e.currentTarget.style.borderColor = "rgba(255,255,255,.06)")}
    >
      {/* Glare effect */}
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 25%, transparent 50%)",
          borderRadius: 14,
          pointerEvents: "none",
          zIndex: 1,
        }}
      />
      <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            display: "grid",
            placeItems: "center",
            background: palette.chip,
            boxShadow: `inset 0 0 0 1px ${palette.ring}`,
          }}
        >
          <div style={{ color: palette.fg, fontSize: 18, lineHeight: 1 }}>{icon}</div>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "#94a3b8", fontSize: 12, fontWeight: 600 }}>{label}</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#e5f0ff" }}>{value ?? "—"}</div>
            {unit ? <div style={{ color: "#9aa6b2", fontWeight: 700 }}>{unit}</div> : null}
          </div>
          {subTag}
        </div>
      </div>
    </Card>
  );
}

const SoldCompsContent = ({ data, marketScope, dealerState }) => {
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  
  const [sortBy, setSortBy] = useState('price');
  const [sortOrder, setSortOrder] = useState('asc');
  const [mileageRange, setMileageRange] = useState([0, 200000]);
  const [activeFilter, setActiveFilter] = useState(null);
  const [similarMileagePct, setSimilarMileagePct] = useState(15);
  const [radius, setRadius] = useState(100);
  const [isSoldIndependentDealersOnly, setIsSoldIndependentDealersOnly] = useState(false);
  const [isVersionMatchOnly, setIsVersionMatchOnly] = useState(false);
  
  // Get version from vehicle
  const vehicleVersion = data?.vehicle_info?.version || data?.vehicle_info?.trim || null;

  // Helper function to get listing state from various possible fields
  const getListingState = (listing) => {
    return listing.state || 
           listing.dealer_state || 
           listing.location?.state || 
           listing.dealer?.state ||
           listing.seller_state;
  };

  // Helper function to check if listing matches dealer state
  const matchesDealerState = (listing) => {
    if (marketScope !== 'my-state' || !dealerState) return true;
    const listingState = getListingState(listing);
    return listingState && listingState.toUpperCase() === dealerState.toUpperCase();
  };

  // Get recent sales data and remove duplicates at source
  // Check both possible locations for sold listings data
  const rawSoldListings = data?.recent_sales || data?.market_analysis?.recent_sales || [];
  
  // Remove duplicates and filter by market scope - memoized to prevent recalculation
  const allSoldListings = useMemo(() => {
    // Remove duplicates at source level using VIN, vdp_url, or combination of fields
    const deduplicated = rawSoldListings.filter((listing, index, self) => {
      const identifier = listing.vin || listing.vdp_url || `${listing.heading}-${listing.price}-${listing.miles}`;
      return index === self.findIndex(l => {
        const lIdentifier = l.vin || l.vdp_url || `${l.heading}-${l.price}-${l.miles}`;
        return lIdentifier === identifier;
      });
    });
    
    // Filter by market scope if needed
    if (marketScope === 'my-state' && dealerState) {
      return deduplicated.filter(listing => {
        const listingState = listing.state || 
                            listing.dealer_state || 
                            listing.location?.state || 
                            listing.dealer?.state ||
                            listing.seller_state;
        return listingState && listingState.toUpperCase() === dealerState.toUpperCase();
      });
    }
    
    return deduplicated;
  }, [rawSoldListings, marketScope, dealerState]);

  
  
  // Calculate dynamic ranges from actual sold listings data
  const allMileages = allSoldListings.map(listing => listing.miles || 0).filter(m => m > 0);
  
  const minMileage = allMileages.length > 0 ? Math.min(...allMileages) : 0;
  const maxMileage = allMileages.length > 0 ? Math.max(...allMileages) : 100000;
  
  
  

  // Initialize ranges when data changes
  useEffect(() => {
    if (minMileage !== maxMileage) {
      setMileageRange([minMileage, maxMileage]);
    } else if (minMileage > 0) {
      // If only one listing, set a range around it
      const range = Math.max(minMileage * 0.1, 1000); // 10% range or at least 1000 miles
      const newRange = [Math.max(0, minMileage - range), minMileage + range];
      setMileageRange(newRange);
    }
  }, [minMileage, maxMileage]);

  // Reset independent dealers and version filters when new search is performed
  useEffect(() => {
    setIsSoldIndependentDealersOnly(false);
    setIsVersionMatchOnly(false);
  }, [data?.vehicle_info?.vin, data?.search_metadata?.vin]);
  // Filter and sort sold listings
  const filteredListings = useMemo(() => {
    // Include listings with no mileage so they still show
    let filtered = allSoldListings.filter(listing => {
      const mileage = listing.miles;
      if (mileage == null) return true;
      return mileage >= mileageRange[0] && mileage <= mileageRange[1];
    });
    
    // Filter by independent dealers only if enabled
    if (isSoldIndependentDealersOnly) {
      filtered = filtered.filter(listing => listing.dealer_type === 'independent');
    }
    
    // Filter by version match only if enabled - exact match on version field only
    if (isVersionMatchOnly && vehicleVersion) {
      const vehicleVersionLower = vehicleVersion.toLowerCase().trim();
      filtered = filtered.filter(listing => {
        const listingVersion = listing.version?.toLowerCase().trim();
        // Match only if version field matches exactly
        return listingVersion && listingVersion === vehicleVersionLower;
      });
    }
    
    return filtered;
  }, [allSoldListings, mileageRange, isSoldIndependentDealersOnly, isVersionMatchOnly, vehicleVersion]);

  // Remove duplicates after filtering
  const uniqueFilteredListings = filteredListings.filter((listing, index, self) => {
    const identifier = listing.vin || listing.vdp_url || `${listing.heading}-${listing.price}-${listing.miles}`;
    return index === self.findIndex(l => {
      const lIdentifier = l.vin || l.vdp_url || `${l.heading}-${l.price}-${l.miles}`;
      return lIdentifier === identifier;
    });
  });

  const sortedListings = useMemo(() => {
    const sorted = [...uniqueFilteredListings];
    const factor = sortOrder === 'asc' ? 1 : -1;
    
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'mileage':
          aValue = a.miles || a.mileage || 0;
          bValue = b.miles || b.mileage || 0;
          break;
        case 'dom':
          aValue = a.dom_active || a.dom || 0;
          bValue = b.dom_active || b.dom || 0;
          break;
        case 'sold_date':
          // Handle sold_date as string or Date object - check multiple possible field names
          const aDate = a.sold_date || a.soldDate || a.date_sold || a.last_seen_at_date || null;
          const bDate = b.sold_date || b.soldDate || b.date_sold || b.last_seen_at_date || null;
          
          // Parse dates and convert to timestamps
          let aTimestamp = null;
          let bTimestamp = null;
          
          if (aDate) {
            const parsedA = new Date(aDate);
            aTimestamp = isNaN(parsedA.getTime()) ? null : parsedA.getTime();
          }
          
          if (bDate) {
            const parsedB = new Date(bDate);
            bTimestamp = isNaN(parsedB.getTime()) ? null : parsedB.getTime();
          }
          
          // Handle null dates - put them at the end (use a very large or small value)
          if (aTimestamp === null && bTimestamp === null) {
            aValue = 0;
            bValue = 0;
          } else if (aTimestamp === null) {
            // a has no date, put it at the end
            aValue = sortOrder === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
            bValue = bTimestamp;
          } else if (bTimestamp === null) {
            // b has no date, put it at the end
            aValue = aTimestamp;
            bValue = sortOrder === 'asc' ? Number.MAX_SAFE_INTEGER : Number.MIN_SAFE_INTEGER;
          } else {
            // Both have valid dates
            aValue = aTimestamp;
            bValue = bTimestamp;
          }
          break;
        case 'price':
        default:
          aValue = a.price || 0;
          bValue = b.price || 0;
          break;
      }
      
      return (aValue - bValue) * factor;
    });
    
    return sorted;
  }, [uniqueFilteredListings, sortBy, sortOrder]);

  // Calculate averages from filtered data
  // Filter out listings without valid prices for average price calculation
  const listingsWithPrice = sortedListings.filter(listing => listing.price && listing.price > 0);
  // Filter out listings without valid mileage for average mileage calculation
  const listingsWithMileage = sortedListings.filter(listing => {
    const mileage = listing.miles || listing.mileage;
    return mileage && mileage > 0;
  });
  
  const avgPrice = listingsWithPrice.length > 0 
    ? Math.round(listingsWithPrice.reduce((sum, listing) => sum + listing.price, 0) / listingsWithPrice.length)
    : 0;
    
  const avgMileage = listingsWithMileage.length > 0 
    ? Math.round(listingsWithMileage.reduce((sum, listing) => sum + (listing.miles || listing.mileage), 0) / listingsWithMileage.length)
    : 0;
    
  const avgDOM = sortedListings.length > 0 
    ? Math.round(sortedListings.reduce((sum, listing) => sum + (listing.dom_active || listing.dom || 0), 0) / sortedListings.length)
    : 0;

  const applySimilarMileageRange = useCallback((pct) => {
    const targetMileage = data?.vehicle?.miles || data?.search_metadata?.mileage || data?.vehicle_info?.mileage;
    const fallbackMileage = avgMileage || 50000;
    const base = targetMileage || fallbackMileage;
    const low = Math.max(0, Math.round(base * (1 - pct / 100)));
    const high = Math.round(base * (1 + pct / 100));
    setMileageRange([low, high]);
    setActiveFilter('similarMileage');
  }, [data?.vehicle?.miles, data?.search_metadata?.mileage, data?.vehicle_info?.mileage, avgMileage]);

  const handleSimilarMileage = () => {
    if (activeFilter === 'similarMileage') {
      setMileageRange([minMileage, maxMileage]);
      setActiveFilter(null);
      setIsMileagePillExpanded(false);
    } else {
      applySimilarMileageRange(similarMileagePct);
      setIsMileagePillExpanded(false);
    }
  };

  const handleMileagePctChange = (pct) => {
    setSimilarMileagePct(pct);
    if (activeFilter === 'similarMileage') applySimilarMileageRange(pct);
  };

  const handleReset = () => {
    // Reset to full ranges based on actual data
    setMileageRange([minMileage, maxMileage]);
    setActiveFilter(null);
    setRadius(100);
    setIsSoldIndependentDealersOnly(false);
    setIsVersionMatchOnly(false);
  };

  return (
    <div style={{ padding: isMobile ? '0 12px' : '0 24px', backgroundColor: 'transparent', maxWidth: '100%', overflowX: 'hidden' }}>
      {/* Location Info */}
      <div style={{ 
        marginBottom: 16, 
        textAlign: 'center',
        color: '#8ea0c6',
        fontSize: 14
      }}>
        <Text style={{ color: '#8ea0c6' }}>
          Listings within {data?.search_metadata?.search_radius || 100} miles of your search location
        </Text>
      </div>

      {/* Controls */}
      <Card
        bordered={false}
        bodyStyle={{ padding: 12, position: "relative", zIndex: 2 }}
        style={{
          borderRadius: 12,
          background: "linear-gradient(180deg, #0f1422, #0b101a)",
          border: "1px solid rgba(255,255,255,.06)",
          boxShadow: "0 10px 26px rgba(0,0,0,.35)",
          marginBottom: 24,
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Glare effect */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 25%, transparent 50%)",
            borderRadius: 12,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        {/* Row A: Sort + Radius + Reset */}
        <div
          style={{
            display: "flex",
            gap: 12,
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <Space size={8} wrap>
            <Text style={{ color: "#9aa6b2" }}>Sort by</Text>
            <Select
              value={sortBy}
              onChange={setSortBy}
              style={{ width: 140, color: "#fff", background: "#181f3a" }}
              dropdownStyle={{ background: "#181f3a" }}
              options={[
                { value: "price", label: "Price" },
                { value: "mileage", label: "Mileage" },
                { value: "dom", label: "Days on Market" },
                { value: "sold_date", label: "Sold Date" },
              ]}
              optionLabelProp="label"
            />
            <Button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              icon={sortOrder === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            >
              {sortOrder === 'asc' ? 'Asc' : 'Desc'}
            </Button>
          </Space>

          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ color: '#9aa6b2', fontSize: 13, fontWeight: 600 }}>Radius:</Text>
              <Select
                value={radius}
                onChange={setRadius}
                size="small"
                options={RADIUS_OPTIONS.map((mi) => ({ value: mi, label: `${mi} mi` }))}
                style={{ width: 88, color: '#fff', background: '#181f3a' }}
                dropdownStyle={{ background: '#181f3a' }}
                dropdownClassName="radius-dropdown"
              />
            </div>
            <Button type="link" icon={<ReloadOutlined />} onClick={handleReset}>
              Reset
            </Button>
          </div>
        </div>

        <Divider style={{ margin: "10px 0", borderColor: "rgba(255,255,255,.08)" }} />

        {/* Row B: Mileage Filter */}
        <div>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Text strong style={{ color: "#e5f0ff" }}>Mileage</Text>
            <Text style={{ color: "#8ea0c6" }}>
              {mileageRange[0].toLocaleString()}–{mileageRange[1].toLocaleString()} mi
            </Text>
          </div>
          <Slider
            range
            min={minMileage}
            max={maxMileage}
            step={500}
            value={mileageRange}
            onChange={(v) => {
              setMileageRange(v);
              setActiveFilter(null);
            }}
            tooltip={{ formatter: (v) => `${v.toLocaleString()} mi` }}
          />
          <div style={{ display: "flex", justifyContent: "flex-start", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div
              className={`mileage-pill ${activeFilter === 'similarMileage' ? 'mileage-pill-selected' : ''}`}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                minHeight: 24,
                padding: '2px 4px 2px 11px',
                borderRadius: 6,
                border: '1px solid rgba(255,255,255,.12)',
                background: activeFilter === 'similarMileage' ? 'linear-gradient(90deg, #6366f1, #22c55e)' : '#181f3a',
                color: '#fff',
                fontSize: 14,
                fontWeight: 600,
                transition: 'background 0.2s',
              }}
            >
              <span
                onClick={handleSimilarMileage}
                style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Use similar mileage
              </span>
              <Select
                value={similarMileagePct}
                onChange={handleMileagePctChange}
                onClick={(e) => e.stopPropagation()}
                size="small"
                options={MILEAGE_VARIANCES.map((p) => ({ value: p, label: `±${p}%` }))}
                style={{
                  width: 72,
                  marginLeft: 6,
                  color: '#fff',
                }}
                className="mileage-pill-select"
                dropdownStyle={{ background: '#181f3a' }}
                dropdownClassName="mileage-pill-dropdown"
              />
            </div>
            <Button
              size="small"
              type={isSoldIndependentDealersOnly ? "primary" : "default"}
              onClick={() => setIsSoldIndependentDealersOnly(!isSoldIndependentDealersOnly)}
              className={isSoldIndependentDealersOnly ? "similar-mileage-selected filter-btn" : "filter-btn"}
            >
              {isSoldIndependentDealersOnly ? '✓ Independent Dealers Only' : 'Independent Dealers Only'}
            </Button>
            {vehicleVersion && (
              <Button
                size="small"
                type={isVersionMatchOnly ? "primary" : "default"}
                onClick={() => setIsVersionMatchOnly(!isVersionMatchOnly)}
                className={isVersionMatchOnly ? "similar-mileage-selected filter-btn" : "filter-btn"}
              >
                {isVersionMatchOnly
                  ? `✓ Show Matching Version Only`
                  : `Show Matching Version Only`}
              </Button>
            )}
          </div>
        </div>
      </Card>

      {/* Summary strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
          gridTemplateRows: isMobile ? "repeat(4, auto)" : "repeat(2, auto)",
          gap: 12,
          marginTop: 12,
          marginBottom: 24,
        }}
      >
        <MetricPro
          tone="indigo"
          icon={<AppstoreOutlined />}
          label="Sold Listings"
          value={sortedListings.length}
        />
        <MetricPro
          tone="blue"
          icon={<DollarOutlined />}
          label="Average Price"
          value={avgPrice ? `$${avgPrice.toLocaleString()}` : "—"}
        />
        <MetricPro
          tone="amber"
          icon={<DashboardOutlined />}
          label="Average Mileage"
          value={avgMileage ? avgMileage.toLocaleString() : "—"}
          unit="mi"
        />
        <MetricPro
          tone="teal"
          icon={<ClockCircleOutlined />}
          label="Average DOM"
          value={avgDOM || "—"}
          unit="days"
        />
      </div>

      {/* Sold Listings */}
      <div>
        {sortedListings.length === 0 ? (
          <Card
            style={{
              borderRadius: 14,
              background: "linear-gradient(180deg, #0f1422, #0b101a)",
              borderColor: "rgba(255,255,255,.06)",
              boxShadow: "0 10px 26px rgba(0,0,0,.35)",
              textAlign: "center",
              padding: "40px 20px",
            }}
          >
            <Text style={{ color: '#a0a3b1', fontSize: 16 }}>
              No sold listings found for this vehicle configuration.
            </Text>
          </Card>
        ) : (
          sortedListings.map((listing) => (
            <Card
              key={listing.vdp_url || `${listing.heading}-${listing.price}-${listing.miles}`}
              bordered
              hoverable
              style={{
                borderRadius: 14,
                background: "linear-gradient(180deg, #0f1422, #0b101a)",
                borderColor: "rgba(255,255,255,.06)",
                boxShadow: "0 10px 26px rgba(0,0,0,.35)",
                marginBottom: 12,
                position: "relative",
                overflow: "hidden",
              }}
              bodyStyle={{ padding: 12, position: "relative", zIndex: 2 }}
            >
              {/* Glare effect */}
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: "linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(255,255,255,0.05) 25%, transparent 50%)",
                  borderRadius: 14,
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                }}
              >
                {/* Vehicle Image - only use listing/API photo; show "Image not available" when missing or load fails */}
                <div
                  style={{
                    width: 80,
                    height: 60,
                    borderRadius: 8,
                    overflow: "hidden",
                    backgroundColor: "#1a1a1a",
                    position: "relative",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {(listing.photo || listing.media?.photo_links_cached?.[0] || listing.media?.photo_links?.[0] || listing.image) ? (
                    <>
                      <img
                        src={listing.photo || listing.media?.photo_links_cached?.[0] || listing.media?.photo_links?.[0] || listing.image}
                        alt={listing.heading || `${listing.build?.year || listing.year} ${listing.build?.make || listing.make} ${listing.build?.model || listing.model}`}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextElementSibling.style.display = "flex";
                        }}
                      />
                      <div style={{
                        width: "100%",
                        height: "100%",
                        display: "none",
                        alignItems: "center",
                        justifyContent: "center",
                        backgroundColor: "#1a1a1a",
                        color: "rgba(255,255,255,0.6)",
                        fontSize: 10,
                        textAlign: "center",
                      }}>
                        Image not available
                      </div>
                    </>
                  ) : (
                    <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 10, textAlign: "center", padding: 4 }}>Image not available</span>
                  )}
                </div>

                {/* Vehicle Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ 
                    fontWeight: 700, 
                    color: "#e5f0ff", 
                    marginBottom: 4,
                    fontSize: 14,
                    lineHeight: 1.3,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}>
                    {(() => {
                      const baseTitle = listing.heading || `${listing.year || ''} ${listing.make || ''} ${listing.model || ''} ${listing.trim || ''}`.trim() || 'Vehicle';
                      if (listing.version) {
                        return (
                          <>
                            {baseTitle}
                            <span style={{ color: "#8ea0c6", fontWeight: 400, margin: "0 4px" }}>•</span>
                            <span style={{ color: "#8ea0c6", fontWeight: 400 }}>{listing.version}</span>
                          </>
                        );
                      }
                      return baseTitle;
                    })()}
                  </div>
                  <div style={{ 
                    marginBottom: 4, 
                    color: "#dbe6ff",
                    fontSize: 13,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 6,
                    alignItems: "center"
                  }}>
                    <span>
                      {listing.price ? `$${listing.price.toLocaleString()}` : "—"}
                    </span>
                    <span style={{ color: '#8ea0c6' }}>•</span>
                    <span style={{ color: "#3b82f6" }}>
                      {(listing.miles || 0).toLocaleString()} mi
                    </span>
                  </div>
                  <div style={{ 
                    color: "#8ea0c6",
                    fontSize: 11,
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    flexWrap: "wrap",
                  }}>
                    <span style={{ 
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      maxWidth: "120px",
                    }}>
                      {listing.dealer_name || listing.dealer?.name || "Unknown"}
                    </span>
                    {(() => {
                      const city = listing.city || listing.dealer_city || listing.location?.city || listing.dealer?.city;
                      const state = listing.state || listing.dealer_state || listing.location?.state || listing.dealer?.state || listing.seller_state;
                      if (city || state) {
                        return (
                          <>
                            <span style={{ color: "#8ea0c6", flexShrink: 0 }}>•</span>
                            <span style={{ color: "#8ea0c6", flexShrink: 0 }}>
                              {city && state ? `${city}, ${state}` : city || state}
                            </span>
                          </>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </div>

                {/* DOM and Sold - stacked vertically on the right */}
                <div
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-end",
                    gap: 4,
                  }}
                >
                  {listing.dom_active != null && listing.dom_active > 0 && (
                    <Tag
                      style={{
                        margin: 0,
                        fontSize: 10,
                        padding: "2px 8px",
                        background: "rgba(147, 51, 234, 0.1)",
                        border: "1px solid rgba(147, 51, 234, 0.3)",
                        color: "#a78bfa",
                      }}
                    >
                      DOM: {listing.dom_active}
                    </Tag>
                  )}
                  {listing.last_seen_at_date && (
                    <Tag
                      color="green"
                      style={{
                        margin: 0,
                        fontSize: 10,
                        padding: "2px 8px",
                        background: "rgba(34, 197, 94, 0.1)",
                        border: "1px solid rgba(34, 197, 94, 0.3)",
                        color: "#22c55e",
                      }}
                    >
                      Sold {new Date(listing.last_seen_at_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </Tag>
                  )}
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      <style>{`
        .ant-select-selector {
          color: #fff !important;
          background: #181f3a !important;
        }
        .ant-select-dropdown {
          background: #181f3a !important;
        }
        .ant-select-item-option-content {
          color: #fff !important;
        }
        .ant-btn[type="link"], .ant-btn:not(.filter-btn) {
          box-shadow: none !important;
          background: transparent !important;
          color: #fff !important; /* Always white text */
          border: 1px solid #8b5cf6 !important;
          transition: border-color 0.2s;
        }
        .ant-btn[type="link"]:active, .ant-btn:not(.filter-btn):active,
        .ant-btn[type="link"]:focus, .ant-btn:not(.filter-btn):focus,
        .ant-btn[type="link"]:hover, .ant-btn:not(.filter-btn):hover {
          background: transparent !important;
          color: #fff !important; /* Always white text */
          box-shadow: none !important;
          border-color: #8b5cf6 !important; /* Purple outline */
          filter: none !important;
          outline: none !important;
        }
        .mileage-pill:hover {
          border-color: rgba(255,255,255,.25);
        }
        .mileage-pill.mileage-pill-selected {
          border: none;
          box-shadow: 0 2px 8px rgba(99,102,241,0.10);
        }
        .mileage-pill .mileage-pill-select.ant-select .ant-select-selector {
          border: none !important;
          background: transparent !important;
          color: #fff !important;
          font-weight: 600;
          min-height: 20px;
          padding: 0 20px 0 4px;
        }
        .mileage-pill .mileage-pill-select.ant-select .ant-select-arrow {
          color: rgba(255,255,255,.8);
        }
        .mileage-pill-dropdown.ant-select-dropdown .ant-select-item-option-content {
          color: #fff;
        }
        .mileage-pill-dropdown.ant-select-dropdown .ant-select-item-option-active,
        .mileage-pill-dropdown.ant-select-dropdown .ant-select-item-option-selected {
          background: rgba(34,197,94,.25);
        }
        .filter-btn {
          transition: background 0.2s, color 0.2s;
        }
        .similar-mileage-selected.filter-btn,
        .within-100-selected.filter-btn {
          background: linear-gradient(90deg, #6366f1, #22c55e) !important;
          color: #fff !important;
          border: none !important;
          box-shadow: 0 2px 8px rgba(99,102,241,0.10);
          font-weight: 700;
        }
        .filter-btn:active,
        .similar-mileage-selected.filter-btn:active,
        .within-100-selected.filter-btn:active {
          background: linear-gradient(90deg, #6366f1, #22c55e) !important;
          color: #fff !important;
          filter: none !important;
          box-shadow: 0 2px 8px rgba(99,102,241,0.10);
        }
        /* Only highlight filter buttons, not sort or reset */
        .ant-btn-primary:not(.filter-btn) {
          background: #1677ff !important;
          color: #fff !important;
          border: none !important;
          box-shadow: none !important;
          font-weight: 700;
        }
      `}</style>
    </div>
  );
};

export default SoldCompsContent;

